import { useState } from 'react'

interface HandJointSelectorProps {
  hand: 'left' | 'right'
  condition: 'pain' | 'swelling'
  selectedJoints: string[]
  onSelectionChange: (joints: string[]) => void
}

// Joint coordinates updated with developer's refined anatomically accurate positions
const JOINT_AREAS = {
  left: [
    // Thumb joints
    { id: 'thumb_cmc', name: 'Thumb CMC', x: 68, y: 76, r: 4 },
    { id: 'thumb_mcp', name: 'Thumb MCP', x: 76, y: 65, r: 4 },
    { id: 'thumb_ip', name: 'Thumb IP', x: 87, y: 56, r: 4 },
    
    // Index finger joints  
    { id: 'index_mcp', name: 'Index MCP', x: 59, y: 44, r: 4 },
    { id: 'index_pip', name: 'Index PIP', x: 61.8, y: 31, r: 4 },
    { id: 'index_dip', name: 'Index DIP', x: 64, y: 18, r: 4 },
    
    // Middle finger joints
    { id: 'middle_mcp', name: 'Middle MCP', x: 42, y: 12, r: 4 },
    { id: 'middle_pip', name: 'Middle PIP', x: 42.5, y: 25, r: 4 },
    { id: 'middle_dip', name: 'Middle DIP', x: 42.8, y: 38.2, r: 4 },
    
    // Ring finger joints
    { id: 'ring_mcp', name: 'Ring MCP', x: 22, y: 18, r: 4 },
    { id: 'ring_pip', name: 'Ring PIP', x: 25.5, y: 31, r: 4 },
    { id: 'ring_dip', name: 'Ring DIP', x: 28, y: 43.8, r: 4 },
    
    // Pinky joints
    { id: 'pinky_mcp', name: 'Pinky MCP', x: 5, y: 30, r: 4 },
    { id: 'pinky_pip', name: 'Pinky PIP', x: 11, y: 42, r: 4 },
    { id: 'pinky_dip', name: 'Pinky DIP', x: 18, y: 53.5, r: 4 }
  ],
  right: [
    // Thumb joints
    { id: 'thumb_cmc', name: 'Thumb CMC', x: 32, y: 76, r: 4 },
    { id: 'thumb_mcp', name: 'Thumb MCP', x: 24, y: 65, r: 4 },
    { id: 'thumb_ip', name: 'Thumb IP', x: 13, y: 56, r: 4 },
    
    // Index finger joints  
    { id: 'index_mcp', name: 'Index MCP', x: 41, y: 44, r: 4 },
    { id: 'index_pip', name: 'Index PIP', x: 38.2, y: 31, r: 4 },
    { id: 'index_dip', name: 'Index DIP', x: 36, y: 18, r: 4 },
    
    // Middle finger joints
    { id: 'middle_mcp', name: 'Middle MCP', x: 57.2, y: 38.2, r: 4 },
    { id: 'middle_pip', name: 'Middle PIP', x: 57.5, y: 25, r: 4 },
    { id: 'middle_dip', name: 'Middle DIP', x: 58, y: 12, r: 4 },
    
    // Ring finger joints
    { id: 'ring_mcp', name: 'Ring MCP', x: 72, y: 43.8, r: 4 },
    { id: 'ring_pip', name: 'Ring PIP', x: 74.5, y: 31, r: 4 },
    { id: 'ring_dip', name: 'Ring DIP', x: 78, y: 18, r: 4 },
    
    // Pinky joints
    { id: 'pinky_mcp', name: 'Pinky MCP', x: 82, y: 53.5, r: 4 },
    { id: 'pinky_pip', name: 'Pinky PIP', x: 89, y: 42, r: 4 },
    { id: 'pinky_dip', name: 'Pinky DIP', x: 95, y: 30, r: 4 }
  ]
}

export default function HandJointSelector({ 
  hand, 
  condition, 
  selectedJoints, 
  onSelectionChange 
}: HandJointSelectorProps) {
  const joints = JOINT_AREAS[hand]
  
  const toggleJoint = (jointId: string) => {
    if (selectedJoints.includes(jointId)) {
      onSelectionChange(selectedJoints.filter(id => id !== jointId))
    } else {
      onSelectionChange([...selectedJoints, jointId])
    }
  }
  
  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <h3 className="text-xl font-semibold text-slate-800">
          {hand.toUpperCase()} HAND
        </h3>
        <p className="text-slate-700">
          Please select which of the following joints {condition === 'pain' ? 'are painful, achy, or stiff' : 'you feel are swollen'}:
        </p>
      </div>
      
      <div className="relative w-full max-w-[300px] mx-auto">
        {/* Use same hand.png image that developer calibrated coordinates against */}
        <img 
          src="/images/hand.png" 
          alt={`${hand} hand diagram`}
          className="w-full h-auto"
          style={{ transform: hand === 'right' ? 'scaleX(-1)' : 'none' }}
        />
        
        {/* Joint circles positioned over the PNG image - exact same styling as developer test */}
        {joints.map(joint => (
          <button
            key={joint.id}
            className={`absolute w-[44px] h-[44px] rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
              selectedJoints.includes(joint.id)
                ? 'bg-red-500 border-red-600 text-white' 
                : 'bg-white border-red-400 hover:border-red-500 hover:bg-red-50'
            }`}
            style={{ 
              left: `${joint.x}%`, 
              top: `${joint.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            onClick={() => toggleJoint(joint.id)}
            title={joint.name}
          />
        ))}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center space-x-6 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-500 rounded-full"></div>
          <span>Selected</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-white border-2 border-gray-500 rounded-full"></div>
          <span>Not</span>
        </div>
      </div>
      
      {/* Selected joints summary */}
      {selectedJoints.length > 0 && (
        <div className="text-center">
          <p className="text-sm text-slate-600 mb-2">
            Selected: {selectedJoints.length} joint{selectedJoints.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  )
}